// lib/impersonation.ts
// Impersonation-Hilfsfunktionen für Admin "Als User einloggen"
//
// Dieses Modul ermöglicht es Admins, die Plattform aus der Sicht eines
// anderen Users zu sehen, ohne dessen Passwort zu kennen.
// Die echte Admin-Session bleibt dabei erhalten.

import { getSupabaseBrowserClient } from '@/lib/supabase'

// Cookie-Name für die Impersonation
export const IMPERSONATE_COOKIE = 'ss_impersonate_user_id'
export const IMPERSONATE_ADMIN_COOKIE = 'ss_impersonate_admin_id'

// ============================================================
// Client-seitige Funktionen
// ============================================================

/**
 * Prüft ob gerade eine Impersonation aktiv ist (Client-side)
 */
export function isImpersonating(): boolean {
  if (typeof document === 'undefined') return false
  return document.cookie.includes(`${IMPERSONATE_COOKIE}=`)
}

/**
 * Liest die impersonierte User-ID aus dem Cookie (Client-side)
 */
export function getImpersonatedUserId(): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${IMPERSONATE_COOKIE}=([^;]+)`)
  )
  return match ? decodeURIComponent(match[1]) : null
}

/**
 * Liest die echte Admin-ID aus dem Cookie (Client-side)
 */
export function getRealAdminId(): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${IMPERSONATE_ADMIN_COOKIE}=([^;]+)`)
  )
  return match ? decodeURIComponent(match[1]) : null
}

/**
 * Startet die Impersonation eines Users
 * Wird über die API-Route aufgerufen
 */
export async function startImpersonation(targetUserId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch('/api/admin/impersonate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetUserId }),
    })

    const data = await response.json()

    if (!response.ok) {
      return { success: false, error: data.error || 'Fehler beim Starten der Impersonation' }
    }

    // Redirect basierend auf Rolle des Ziel-Users
    const targetRole = data.targetUser?.role
    if (targetRole === 'bestatter' || targetRole === 'subadmin' || targetRole === 'sds_user') {
      window.location.href = '/admin/dashboard'
    } else {
      window.location.href = '/dashboard/user'
    }
    return { success: true }
  } catch (error) {
    console.error('Impersonation error:', error)
    return { success: false, error: 'Verbindungsfehler' }
  }
}

/**
 * Beendet die Impersonation und kehrt zum Admin zurück
 */
export async function stopImpersonation(): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch('/api/admin/impersonate', {
      method: 'DELETE',
    })

    const data = await response.json()

    if (!response.ok) {
      return { success: false, error: data.error || 'Fehler beim Beenden der Impersonation' }
    }

    // Zurück zum Admin Dashboard
    window.location.href = '/admin/users'
    return { success: true }
  } catch (error) {
    console.error('Stop impersonation error:', error)
    return { success: false, error: 'Verbindungsfehler' }
  }
}

// ============================================================
// Server-seitige Funktionen (für API Routes und Middleware)
// ============================================================

/**
 * Liest die impersonierte User-ID aus den Request-Cookies (Server-side)
 */
export function getImpersonatedUserIdFromCookies(
  cookies: { get: (name: string) => { value: string } | undefined }
): string | null {
  const cookie = cookies.get(IMPERSONATE_COOKIE)
  return cookie?.value || null
}

/**
 * Liest die echte Admin-ID aus den Request-Cookies (Server-side)
 */
export function getRealAdminIdFromCookies(
  cookies: { get: (name: string) => { value: string } | undefined }
): string | null {
  const cookie = cookies.get(IMPERSONATE_ADMIN_COOKIE)
  return cookie?.value || null
}
